import { useAuthStore } from "../stores/auth"
import { isPublicRoutePath } from '~/utils/public-routes'

export default defineNuxtPlugin((nuxtApp) => {
  const router = useRouter()
  const config = useRuntimeConfig()
  const apiBase = config.public.apiBase as string

  // Dedup in-flight refreshes. Without this, a stale-token cold load on a
  // dashboard with multiple parallel queries fires N concurrent refresh
  // requests (one query + plugin mount + nav guard), each producing its
  // own 401 in the console. Collapse them onto a single shared promise.
  let refreshInFlight: Promise<boolean> | null = null
  // Timestamp of the most recent successful refresh. A second cooldown
  // collapses the *sequential* burst that happens within the first ~second
  // of a navigation (plugin mount + middleware + the initial useCustomFetch
  // query each call checkTokenExpiration in quick succession). The 30s
  // interval and visibilitychange wake-ups sit comfortably outside this
  // window and still fire real refreshes.
  let lastSuccessAt = 0
  const SEQUENTIAL_COOLDOWN_MS = 2000
  // After clearAuth + redirect to /login we don't want any pending listener
  // (interval / visibilitychange) to fire another check.
  let stopped = false

  // performRefresh owns the in-flight lock so no caller can accidentally
  // bypass dedup by calling it directly. Two callers landing here at the
  // same instant both await the same promise.
  const performRefresh = (): Promise<boolean> => {
    if (refreshInFlight) return refreshInFlight
    refreshInFlight = (async () => {
      try {
        await $fetch(`${apiBase}/auth/refresh`, {
          method: 'GET',
          credentials: 'include',
        })
        return true
      } catch (error) {
        console.error('Token validation failed:', error)
        return false
      }
    })().finally(() => {
      refreshInFlight = null
    })
    return refreshInFlight
  }

  // Shared refresh: every caller (plugin, middleware, useCustomFetch retry)
  // awaits the same promise while a refresh is in flight. Layered on top of
  // performRefresh, it also stamps the cooldown and invalidates queries on
  // success.
  const refreshToken = async (): Promise<boolean> => {
    const ok = await performRefresh()
    if (ok) {
      lastSuccessAt = Date.now()
      const queryClient: any = nuxtApp.$queryClient
      await queryClient?.invalidateQueries?.()
    }
    return ok
  }

  // Check token validity. On failure, kicks off logout if we're on a
  // protected route. Skips the network call when a refresh succeeded in
  // the last SEQUENTIAL_COOLDOWN_MS — that's how the burst of plugin-mount
  // + middleware + initial query calls collapses to a single round trip.
  // useCustomFetch's 401 retry path goes through refreshToken() directly,
  // so a genuinely expired access token still triggers a refresh even
  // inside the cooldown window.
  //
  // `path` is optional: navigation guards should pass `to.path` because
  // `router.currentRoute` still points at `from` mid-navigation.
  const checkTokenExpiration = async (path?: string): Promise<boolean> => {
    if (stopped) return false
    if (!refreshInFlight && Date.now() - lastSuccessAt < SEQUENTIAL_COOLDOWN_MS) {
      return true
    }
    const ok = await refreshToken()
    const targetPath = path ?? router.currentRoute.value.path
    if (!ok && !isPublicRoutePath(targetPath)) {
      await handleLogout()
    }
    return ok
  }

  const handleLogout = async (): Promise<void> => {
    stopped = true
    if (intervalId) {
      clearInterval(intervalId)
      intervalId = null
    }
    try {
      await $fetch(`${apiBase}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      })
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      const queryClient: any = nuxtApp.$queryClient
      if (queryClient) {
        queryClient.clear()
      }

      const authStore = useAuthStore()
      authStore.clearAuth()

      if (router.currentRoute.value.path !== '/login') {
        await router.push('/login')
      }
    }
  }

  let intervalId: ReturnType<typeof setInterval> | null = null

  if (process.client) {
    if (!isPublicRoutePath(router.currentRoute.value.path)) {
      checkTokenExpiration()
    }

    intervalId = setInterval(() => {
      if (stopped) return
      if (!isPublicRoutePath(router.currentRoute.value.path)) {
        checkTokenExpiration()
      }
    }, 30000)

    document.addEventListener('visibilitychange', () => {
      if (stopped) return
      if (!document.hidden && !isPublicRoutePath(router.currentRoute.value.path)) {
        checkTokenExpiration()
      }
    })

    window.addEventListener('beforeunload', () => {
      if (intervalId) {
        clearInterval(intervalId)
        intervalId = null
      }
    })

    // Route gating lives in middleware/auth.global.ts — keeping a duplicate
    // router.beforeEach here was the second sequential refresh on every
    // protected nav.
  }

  return {
    provide: {
      checkAuth: checkTokenExpiration,
      refreshToken: refreshToken,
      logout: handleLogout,
    }
  }
})
